// src/pages/Auth/LoginPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Boxes,
  BrainCircuit,
  Building2,
  CalendarDays,
  Check,
  CircleAlert,
  Eye,
  EyeOff,
  LoaderCircle,
  LockKeyhole,
  Mail,
  PackageCheck,
  ReceiptText,
  ShieldCheck,
  Sparkles,
  Warehouse,
  X,
} from "lucide-react";
import { useAuth } from "@/context/AuthProvider";

/* ───────────────────────── visual data ───────────────────────── */

const operationalNodes = [
  {
    className: "solo-auth-node--procurement",
    title: "Procurement",
    detail: "Plan · Approve · Order",
    icon: Boxes,
  },
  {
    className: "solo-auth-node--receiving",
    title: "Goods Receiving",
    detail: "Receive · Inspect · Resolve",
    icon: PackageCheck,
  },
  {
    className: "solo-auth-node--inventory",
    title: "Inventory",
    detail: "Track · Move · Replenish",
    icon: Warehouse,
  },
  {
    className: "solo-auth-node--invoices",
    title: "Invoices",
    detail: "Capture · Review · Approve",
    icon: ReceiptText,
  },
  {
    className: "solo-auth-node--events",
    title: "Events",
    detail: "Plan · Consume · Analyse",
    icon: CalendarDays,
  },
  {
    className: "solo-auth-node--intelligence",
    title: "Intelligence",
    detail: "See · Understand · Decide",
    icon: BrainCircuit,
  },
];

const ALLOWED_RETURN_PREFIXES = Object.freeze([
  "/dashboard",
  "/vendors",
  "/procurement",
  "/inventory",
  "/inventory-dashboard",
  "/staffing-dashboard",
  "/maintenance",
  "/event-menu-planner",
  "/events",
  "/planners",
  "/admin-timesheet-dashboard",
  "/admin",
  "/menu",
  "/invoice",
  "/client-admin",
]);

/* ───────────────────────── helpers ───────────────────────── */

function safePath(p) {
  const s = String(p || "").trim();
  if (!s) return "";
  // Only allow internal paths.
  if (!s.startsWith("/")) return "";
  // Avoid redirect loops.
  if (
    s.startsWith("/login") ||
    s.startsWith("/forbidden") ||
    s.startsWith("/unauthorized")
  )
    return "";
  return s;
}

function buildFromState(from) {
  try {
    if (!from) return "";
    const p = String(from.pathname || "");
    const s = String(from.search || "");
    const h = String(from.hash || "");
    return `${p}${s}${h}` || "";
  } catch {
    return "";
  }
}

function isAllowedReturnTo(path) {
  const candidate = safePath(path);
  if (!candidate) return false;
  return ALLOWED_RETURN_PREFIXES.some(
    (base) => candidate === base || candidate.startsWith(`${base}/`),
  );
}

export default function LoginPage() {
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Local-storage helpers must never block authentication if storage is unavailable.
  const safeGet = (key, fallback = null) => {
    try {
      const value = localStorage.getItem(key);
      return value == null ? fallback : value;
    } catch {
      return fallback;
    }
  };

  const safeSet = (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Storage is optional; authentication can continue without persistence.
    }
  };

  const safeRemove = (key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage is optional; authentication can continue without persistence.
    }
  };

  function clearUnitContext() {
    try {
      localStorage.removeItem("solo:current-unit");
      localStorage.removeItem("unit:slug");
      localStorage.removeItem("activeUnitId");
      localStorage.removeItem("activeUnitSlug");
      // Keep auth:lastCompanyId on purpose.
    } catch {
      // A restricted storage environment must not block sign-in.
    }
  }

  /**
   * Seed tenant context only from the authenticated backend response.
   */
  function seedUnitContextFromUser(user) {
    try {
      const companyId =
        user?.company_id != null ? Number(user.company_id) : null;
      const unitId = user?.unit_id != null ? Number(user.unit_id) : null;
      const unitSlug = user?.unit_slug ? String(user.unit_slug) : "";

      if (companyId) safeSet("auth:lastCompanyId", String(companyId));
      if (user?.email) safeSet("auth:lastEmail", String(user.email));

      if (companyId && unitId) {
        const context = {
          id: unitId,
          slug: unitSlug || "",
          company_id: companyId,
        };
        safeSet("solo:current-unit", JSON.stringify(context));
        if (context.slug) safeSet("unit:slug", context.slug);

        // Legacy mirrors retained for existing application consumers.
        safeSet("activeUnitId", String(unitId));
        if (context.slug) safeSet("activeUnitSlug", context.slug);
      }
    } catch {
      // Tenant context will be resolved again by authenticated API requests.
    }
  }

  /**
   * Resolve the destination after login in this order:
   *  1) ?returnTo=...
   *  2) location.state.returnTo
   *  3) location.state.from
   */
  const returnTo = useMemo(() => {
    let queryReturnTo = "";
    try {
      const params = new URLSearchParams(location.search || "");
      queryReturnTo = params.get("returnTo") || "";
    } catch {
      // Invalid query data falls through to the router-state destinations.
    }

    if (queryReturnTo) {
      try {
        queryReturnTo = decodeURIComponent(queryReturnTo);
      } catch {
        // Validate the original value below if it was already decoded.
      }
      if (isAllowedReturnTo(queryReturnTo)) return queryReturnTo;
    }

    const stateReturnTo = safePath(location.state?.returnTo || "");
    if (stateReturnTo && isAllowedReturnTo(stateReturnTo)) return stateReturnTo;

    const from = buildFromState(location.state?.from);
    if (from && isAllowedReturnTo(from)) return from;

    return "/dashboard";
  }, [location.search, location.state]);

  const [form, setForm] = useState({
    email: "",
    password: "",
    company_id: "",
    remember: true,
  });

  const [error, setError] = useState("");
  const [showPw, setShowPw] = useState(false);

  // Unit picker modal state.
  const [unitModalOpen, setUnitModalOpen] = useState(false);
  const [unitChoices, setUnitChoices] = useState([]);
  const [pendingLogin, setPendingLogin] = useState(null);

  // Prefill the last company and email when the user chose to remember them.
  useEffect(() => {
    const lastCompanyId = safeGet("auth:lastCompanyId", "4");
    const lastEmail = safeGet("auth:lastEmail", "");
    setForm((current) => ({
      ...current,
      company_id: lastCompanyId,
      email: lastEmail,
    }));
  }, []);

  // Listen for tenant-context events dispatched by the API client.
  useEffect(() => {
    const onUnitRequired = (event) => {
      const units = event?.detail?.units || [];
      if (Array.isArray(units) && units.length > 0) {
        setUnitChoices(units);
        setUnitModalOpen(true);
        setError("");
      }
    };

    const onInvalidUnit = (event) => {
      clearUnitContext();
      const units = event?.detail?.units || [];
      if (Array.isArray(units) && units.length > 0) {
        setUnitChoices(units);
        setUnitModalOpen(true);
        setError("Please select a valid unit for this company.");
      } else {
        setError("Invalid unit context. Please try again.");
      }
    };

    window.addEventListener("auth:unit-required", onUnitRequired);
    window.addEventListener("auth:invalid-unit", onInvalidUnit);
    return () => {
      window.removeEventListener("auth:unit-required", onUnitRequired);
      window.removeEventListener("auth:invalid-unit", onInvalidUnit);
    };
  }, []);

  const onChange = (event) => {
    const { name, value, type, checked } = event.target;
    if (error) setError("");
    const nextValue = name === "company_id" ? value.replace(/\D/g, "") : value;

    // Changing company must remove any stale unit context.
    if (name === "company_id") {
      clearUnitContext();
      setUnitModalOpen(false);
      setUnitChoices([]);
      setPendingLogin(null);
    }

    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : nextValue,
    }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");

    const companyIdInt = Number(String(form.company_id || "").trim());
    if (!Number.isInteger(companyIdInt) || companyIdInt <= 0) {
      setError("Please enter a valid Company ID.");
      return;
    }

    const payload = {
      email: form.email.trim(),
      password: form.password,
      company_id: companyIdInt,
    };

    // Retain credentials in memory only for a possible UNIT_REQUIRED retry.
    setPendingLogin(payload);

    try {
      const response = await login(payload);

      if (response?.ok) {
        const user =
          response.user ||
          response?.data?.user ||
          response?.data?.data?.user ||
          null;
        if (user) seedUnitContextFromUser(user);

        if (form.remember) {
          safeSet("auth:lastCompanyId", String(companyIdInt));
          safeSet("auth:lastEmail", payload.email);
        } else {
          safeRemove("auth:lastCompanyId");
          safeRemove("auth:lastEmail");
        }

        navigate(returnTo, { replace: true });
        return;
      }

      const message =
        response?.error ||
        response?.message ||
        response?.data?.error ||
        response?.data?.message ||
        "Login failed";
      setError(String(message));
    } catch (requestError) {
      const message =
        requestError?.response?.data?.error ||
        requestError?.response?.data?.message ||
        requestError?.message ||
        "Login failed";
      setError(String(message));
    }
  };

  async function chooseUnitAndLogin(unit) {
    if (!pendingLogin) {
      setError("Please sign in again.");
      setUnitModalOpen(false);
      return;
    }

    const unitId = Number(unit?.id);
    const unitSlug = unit?.slug ? String(unit.slug) : "";

    if (!Number.isInteger(unitId) || unitId <= 0) {
      setError("Invalid unit selection.");
      return;
    }

    setError("");
    try {
      const payload = {
        ...pendingLogin,
        unit_id: unitId,
        unit_slug: unitSlug || undefined,
      };

      const response = await login(payload);

      if (response?.ok) {
        const user =
          response.user ||
          response?.data?.user ||
          response?.data?.data?.user ||
          null;
        if (user) seedUnitContextFromUser(user);

        // Seed the selected unit immediately for existing application consumers.
        const companyIdInt = Number(payload.company_id);
        safeSet(
          "solo:current-unit",
          JSON.stringify({
            id: unitId,
            slug: unitSlug,
            company_id: companyIdInt,
          }),
        );
        if (unitSlug) safeSet("unit:slug", unitSlug);
        safeSet("activeUnitId", String(unitId));
        if (unitSlug) safeSet("activeUnitSlug", unitSlug);

        setUnitModalOpen(false);
        navigate(returnTo, { replace: true });
        return;
      }

      const message =
        response?.error ||
        response?.message ||
        response?.data?.error ||
        response?.data?.message ||
        "Login failed";
      setError(String(message));
    } catch (requestError) {
      const message =
        requestError?.response?.data?.error ||
        requestError?.response?.data?.message ||
        requestError?.message ||
        "Login failed";
      setError(String(message));
    }
  }

  const disabled =
    loading ||
    !form.email.trim() ||
    !form.password ||
    !String(form.company_id || "").trim();

  // Subtle pointer tilt on capable devices; disabled for touch and reduced motion.
  const cardRef = useRef(null);
  const animationFrameRef = useRef(0);

  useEffect(
    () => () => {
      cancelAnimationFrame(animationFrameRef.current);
    },
    [],
  );

  const onTilt = (event) => {
    const element = cardRef.current;
    if (!element) return;
    if (
      !window.matchMedia(
        "(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)",
      ).matches
    ) {
      return;
    }

    const rect = element.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const rotateX = (y / rect.height - 0.5) * -3.5;
    const rotateY = (x / rect.width - 0.5) * 3.5;

    cancelAnimationFrame(animationFrameRef.current);
    animationFrameRef.current = requestAnimationFrame(() => {
      element.style.setProperty("--rx", `${rotateX}deg`);
      element.style.setProperty("--ry", `${rotateY}deg`);
    });
  };

  const resetTilt = () => {
    const element = cardRef.current;
    if (!element) return;
    cancelAnimationFrame(animationFrameRef.current);
    element.style.setProperty("--rx", "0deg");
    element.style.setProperty("--ry", "0deg");
  };

  return (
    <div className="solo-auth-shell relative min-h-[100dvh] overflow-x-hidden bg-[#04101f] text-white">
      {unitModalOpen && (
        <div
          className="solo-auth-modal-backdrop fixed inset-0 z-50 grid place-items-center p-4"
          role="presentation"
        >
          <section
            className="solo-auth-modal w-full max-w-lg overflow-hidden rounded-[28px] border border-cyan-100/15 p-5 shadow-2xl sm:p-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="unit-picker-title"
            aria-describedby="unit-picker-description"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl border border-cyan-100/15 bg-cyan-100/[0.06] text-[#7cecdf]">
                  <Building2 size={19} aria-hidden="true" />
                </span>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#78e9dc]">
                    Workspace access
                  </p>
                  <h2
                    id="unit-picker-title"
                    className="mt-1 text-xl font-semibold tracking-[-0.03em]"
                  >
                    Select a unit
                  </h2>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  setUnitModalOpen(false);
                  setUnitChoices([]);
                }}
                className="grid h-9 w-9 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-white/55 hover:border-white/20 hover:bg-white/[0.08] hover:text-white"
                aria-label="Close unit selection"
              >
                <X size={16} aria-hidden="true" />
              </button>
            </div>

            <p
              id="unit-picker-description"
              className="mt-4 text-sm leading-6 text-white/55"
            >
              This company has multiple operational units. Choose where you want
              to continue.
            </p>

            <div className="mt-5 max-h-80 space-y-2 overflow-y-auto pr-1">
              {unitChoices.map((unit) => (
                <button
                  key={unit.id}
                  type="button"
                  onClick={() => chooseUnitAndLogin(unit)}
                  className="group flex w-full items-center justify-between gap-4 rounded-2xl border border-cyan-100/[0.09] bg-[#07172a]/65 px-4 py-3.5 text-left hover:border-cyan-100/25 hover:bg-cyan-100/[0.06]"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-white/90">
                      {unit.unit_name || `Unit #${unit.id}`}
                    </span>
                    <span className="mt-1 block truncate text-xs text-white/[0.38]">
                      {unit.slug || "Unit workspace"}
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-2 text-xs text-white/[0.38] transition group-hover:text-[#8ff4e5]">
                    #{unit.id} <ArrowRight size={14} aria-hidden="true" />
                  </span>
                </button>
              ))}

              {!unitChoices.length && (
                <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] px-4 py-4 text-sm text-white/60">
                  No units are available for this company.
                </div>
              )}
            </div>
          </section>
        </div>
      )}

      <div className="pointer-events-none fixed inset-0" aria-hidden="true">
        <div className="solo-auth-aurora solo-auth-aurora--teal" />
        <div className="solo-auth-aurora solo-auth-aurora--cyan" />
        <div className="solo-auth-aurora solo-auth-aurora--violet" />
        <div className="solo-auth-grid absolute inset-0" />
        <div className="solo-auth-stars absolute inset-0" />
        <div className="solo-auth-noise absolute inset-0" />
        <div className="solo-auth-horizon" />
      </div>

      <main className="relative z-10 mx-auto grid min-h-[100dvh] w-full max-w-[1600px] lg:grid-cols-[minmax(0,1.08fr)_minmax(440px,0.92fr)]">
        <section className="relative flex min-w-0 flex-col px-5 pb-2 pt-7 sm:px-10 sm:pt-9 lg:min-h-[100dvh] lg:px-12 lg:py-10 xl:px-16 2xl:px-20">
          <header className="flex items-center justify-between gap-4">
            <div className="inline-flex items-center gap-3">
              <span className="solo-auth-logo grid h-10 w-10 place-items-center rounded-2xl text-[#041119]">
                <Sparkles size={19} strokeWidth={2.3} aria-hidden="true" />
              </span>
              <span>
                <span className="block text-sm font-semibold tracking-[0.16em] text-white">
                  SOLO.MDSH
                </span>
                <span className="mt-0.5 block text-[9px] font-semibold uppercase tracking-[0.22em] text-white/35">
                  by SOLO.MD
                </span>
              </span>
            </div>

            <div className="hidden items-center gap-2 rounded-full border border-cyan-100/10 bg-[#07172a]/45 px-3 py-2 text-[10px] font-medium uppercase tracking-[0.14em] text-white/45 backdrop-blur-xl sm:flex">
              <span className="h-1.5 w-1.5 rounded-full bg-[#39e8d5] shadow-[0_0_12px_rgba(57,232,213,0.8)]" />
              Secure access
            </div>
          </header>

          <div className="flex flex-1 flex-col justify-center py-10 lg:py-5">
            <div className="relative z-10 max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan-100/15 bg-cyan-100/[0.045] px-3.5 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#9ef7ea] backdrop-blur-xl">
                <Sparkles size={12} aria-hidden="true" />
                One connected operational core
              </div>

              <h1 className="mt-5 max-w-2xl text-[clamp(2.8rem,5.4vw,5.4rem)] font-semibold leading-[0.91] tracking-[-0.058em] text-white">
                Run operations with{" "}
                <span className="solo-auth-gradient-text">real control.</span>
              </h1>

              <p className="mt-5 max-w-xl text-base leading-7 text-white/55 sm:text-lg sm:leading-8">
                Procurement, receiving, inventory, events, invoices and
                intelligence — connected around one operational truth.
              </p>

              <div className="mt-6 flex flex-wrap gap-2.5">
                {[
                  "Unified operations",
                  "Real-time visibility",
                  "Built for scale",
                ].map((benefit) => (
                  <span
                    key={benefit}
                    className="inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-[#07172a]/45 px-3 py-2 text-[11px] text-white/[0.48] backdrop-blur-xl"
                  >
                    <Check
                      size={12}
                      className="text-[#70eadb]"
                      aria-hidden="true"
                    />
                    {benefit}
                  </span>
                ))}
              </div>
            </div>

            <div
              className="solo-auth-visual relative mt-5 h-[320px] w-full max-w-[760px] self-center"
              aria-hidden="true"
            >
              <svg
                className="solo-auth-connectors absolute inset-0 h-full w-full"
                viewBox="0 0 720 320"
                preserveAspectRatio="none"
              >
                <defs>
                  <linearGradient
                    id="solo-auth-line"
                    x1="0"
                    y1="0"
                    x2="1"
                    y2="1"
                  >
                    <stop offset="0%" stopColor="#22e6d3" stopOpacity="0.14" />
                    <stop offset="50%" stopColor="#37c8ff" stopOpacity="0.75" />
                    <stop
                      offset="100%"
                      stopColor="#8b6dff"
                      stopOpacity="0.28"
                    />
                  </linearGradient>
                </defs>
                <path d="M360 160 L124 54" />
                <path d="M360 160 L596 50" />
                <path d="M360 160 L622 160" />
                <path d="M360 160 L592 273" />
                <path d="M360 160 L132 272" />
                <path d="M360 160 L112 160" />
              </svg>

              <div className="solo-auth-orbit solo-auth-orbit--one" />
              <div className="solo-auth-orbit solo-auth-orbit--two" />
              <div className="solo-auth-orbit solo-auth-orbit--three" />

              <div className="solo-auth-core">
                <span className="solo-auth-core-icon">
                  <BrainCircuit size={23} aria-hidden="true" />
                </span>
                <span className="text-[9px] font-semibold uppercase tracking-[0.17em] text-white/[0.38]">
                  Operational core
                </span>
                <strong className="mt-1.5 text-xl tracking-[-0.03em]">
                  SOLO.MDSH
                </strong>
                <span className="mt-1 text-[10px] text-white/[0.32]">
                  Connected · Live · Intelligent
                </span>
              </div>

              {operationalNodes.map((node) => (
                <div
                  key={node.title}
                  className={`solo-auth-node ${node.className}`}
                >
                  {React.createElement(node.icon, {
                    size: 15,
                    "aria-hidden": "true",
                  })}
                  <span className="min-w-0">
                    <strong className="block truncate text-[11px] font-semibold text-white/80">
                      {node.title}
                    </strong>
                    <small className="mt-1 block truncate text-[8px] tracking-[0.02em] text-white/35">
                      {node.detail}
                    </small>
                  </span>
                </div>
              ))}
            </div>
          </div>

          <footer className="hidden items-center gap-2 text-[10px] uppercase tracking-[0.15em] text-white/[0.28] lg:flex">
            <ShieldCheck
              size={13}
              className="text-[#62ddcf]"
              aria-hidden="true"
            />
            Permission-aware · Tenant-isolated · Secure by design
          </footer>
        </section>

        <section className="relative flex items-center justify-center px-4 pb-8 pt-2 sm:px-8 sm:pb-10 lg:min-h-[100dvh] lg:border-l lg:border-cyan-100/[0.07] lg:bg-[#040d1b]/20 lg:px-10 lg:py-10">
          <div className="pointer-events-none absolute inset-0 hidden bg-[radial-gradient(circle_at_50%_45%,rgba(55,200,255,0.07),transparent_44%)] lg:block" />

          <div className="relative w-full max-w-[520px]">
            <div
              className="solo-auth-card-halo pointer-events-none absolute -inset-8 rounded-[48px]"
              aria-hidden="true"
            />

            <form
              ref={cardRef}
              onSubmit={submit}
              onMouseMove={onTilt}
              onMouseLeave={resetTilt}
              className="solo-auth-card relative overflow-hidden rounded-[30px] border border-cyan-100/15 p-5 shadow-2xl sm:p-7 lg:p-8"
              aria-busy={loading}
            >
              <div
                className="solo-auth-card-shine pointer-events-none absolute inset-x-0 top-0 h-px"
                aria-hidden="true"
              />
              <div
                className="pointer-events-none absolute -right-24 -top-24 h-56 w-56 rounded-full bg-[#8b6dff]/10 blur-3xl"
                aria-hidden="true"
              />
              <div
                className="pointer-events-none absolute -bottom-28 -left-24 h-56 w-56 rounded-full bg-[#22e6d3]/[0.08] blur-3xl"
                aria-hidden="true"
              />

              <div className="relative">
                <div className="flex items-center justify-between gap-4">
                  <div className="inline-flex items-center gap-2 rounded-full border border-cyan-100/10 bg-cyan-100/[0.04] px-3 py-2 text-[9px] font-semibold uppercase tracking-[0.17em] text-[#82ecdf]">
                    <LockKeyhole size={11} aria-hidden="true" />
                    Secure workspace access
                  </div>
                  <span className="grid h-9 w-9 place-items-center rounded-2xl border border-cyan-100/10 bg-cyan-100/[0.04] text-[#70e8da]">
                    <BrainCircuit size={17} aria-hidden="true" />
                  </span>
                </div>

                <h2 className="mt-6 text-3xl font-semibold tracking-[-0.045em] text-white sm:text-[2.15rem]">
                  Welcome back.
                </h2>
                <p className="mt-2 text-sm leading-6 text-white/[0.48]">
                  Sign in to continue to your operational workspace.
                </p>

                <div className="mt-4 flex items-center gap-2 rounded-2xl border border-white/[0.07] bg-[#061426]/50 px-3.5 py-3 text-xs text-white/[0.42]">
                  <ShieldCheck
                    size={14}
                    className="shrink-0 text-[#68ded1]"
                    aria-hidden="true"
                  />
                  <span className="min-w-0 truncate">
                    Secure destination:{" "}
                    <span className="font-mono text-white/[0.58]">
                      {returnTo}
                    </span>
                  </span>
                </div>

                <div className="mt-6 space-y-4">
                  <label className="block" htmlFor="solo-company-id">
                    <span className="mb-2 block text-xs font-medium text-white/[0.62]">
                      Company ID
                    </span>
                    <span className="solo-auth-input-shell flex items-center rounded-2xl border border-cyan-100/10 bg-[#061426]/[0.62]">
                      <Building2
                        size={16}
                        className="ml-3.5 shrink-0 text-white/30"
                        aria-hidden="true"
                      />
                      <input
                        id="solo-company-id"
                        name="company_id"
                        inputMode="numeric"
                        pattern="\d*"
                        autoComplete="organization"
                        className="solo-auth-input min-w-0 flex-1 border-0 bg-transparent px-3 py-3.5 text-sm text-white outline-none ring-0 placeholder:text-white/[0.24] focus:border-0 focus:ring-0"
                        placeholder="Enter your company ID"
                        value={form.company_id}
                        onChange={onChange}
                        required
                      />
                    </span>
                  </label>

                  <label className="block" htmlFor="solo-email">
                    <span className="mb-2 block text-xs font-medium text-white/[0.62]">
                      Email address
                    </span>
                    <span className="solo-auth-input-shell flex items-center rounded-2xl border border-cyan-100/10 bg-[#061426]/[0.62]">
                      <Mail
                        size={16}
                        className="ml-3.5 shrink-0 text-white/30"
                        aria-hidden="true"
                      />
                      <input
                        id="solo-email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        className="solo-auth-input min-w-0 flex-1 border-0 bg-transparent px-3 py-3.5 text-sm text-white outline-none ring-0 placeholder:text-white/[0.24] focus:border-0 focus:ring-0"
                        placeholder="you@company.com"
                        value={form.email}
                        onChange={onChange}
                        required
                      />
                    </span>
                  </label>

                  <label className="block" htmlFor="solo-password">
                    <span className="mb-2 block text-xs font-medium text-white/[0.62]">
                      Password
                    </span>
                    <span className="solo-auth-input-shell flex items-center rounded-2xl border border-cyan-100/10 bg-[#061426]/[0.62]">
                      <LockKeyhole
                        size={16}
                        className="ml-3.5 shrink-0 text-white/30"
                        aria-hidden="true"
                      />
                      <input
                        id="solo-password"
                        name="password"
                        type={showPw ? "text" : "password"}
                        autoComplete="current-password"
                        className="solo-auth-input min-w-0 flex-1 border-0 bg-transparent px-3 py-3.5 text-sm text-white outline-none ring-0 placeholder:text-white/[0.24] focus:border-0 focus:ring-0"
                        placeholder="Enter your password"
                        value={form.password}
                        onChange={onChange}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPw((current) => !current)}
                        className="mr-2 grid h-9 w-9 shrink-0 place-items-center rounded-xl text-white/35 hover:bg-white/[0.06] hover:text-white/75"
                        aria-label={showPw ? "Hide password" : "Show password"}
                        aria-pressed={showPw}
                      >
                        {showPw ? (
                          <EyeOff size={16} aria-hidden="true" />
                        ) : (
                          <Eye size={16} aria-hidden="true" />
                        )}
                      </button>
                    </span>
                  </label>
                </div>

                <div className="mt-4 flex items-center justify-between gap-4 text-xs">
                  <label className="group flex cursor-pointer items-center gap-2.5 text-white/55">
                    <input
                      type="checkbox"
                      name="remember"
                      checked={form.remember}
                      onChange={onChange}
                      className="h-4 w-4 rounded border-white/20 bg-[#061426] text-[#27d7ca] focus:ring-[#27d7ca]/30 focus:ring-offset-0"
                    />
                    <span className="transition group-hover:text-white/75">
                      Remember me
                    </span>
                  </label>

                  <Link
                    to={`/forgot-password?company_id=${encodeURIComponent(
                      String(form.company_id || "").trim(),
                    )}&email=${encodeURIComponent(String(form.email || "").trim())}`}
                    className="font-medium text-[#84eadf] transition hover:text-[#b4fff4]"
                  >
                    Forgot password?
                  </Link>
                </div>

                {error && (
                  <div
                    className="mt-4 flex items-start gap-2.5 rounded-2xl border border-red-400/20 bg-red-500/[0.08] px-3.5 py-3 text-sm leading-5 text-red-200"
                    role="alert"
                    aria-live="assertive"
                  >
                    <CircleAlert
                      size={16}
                      className="mt-0.5 shrink-0"
                      aria-hidden="true"
                    />
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={disabled}
                  className={`solo-auth-submit group relative mt-5 flex w-full items-center justify-center gap-2 overflow-hidden rounded-2xl px-5 py-3.5 text-sm font-semibold transition ${
                    disabled
                      ? "cursor-not-allowed border border-white/[0.06] bg-white/[0.04] text-white/30"
                      : "bg-[#35e6dc] text-[#041119] shadow-[0_0_38px_rgba(53,230,220,0.18)] hover:bg-[#8ff4e5]"
                  }`}
                >
                  {!disabled && (
                    <span
                      className="solo-auth-submit-glow absolute inset-0"
                      aria-hidden="true"
                    />
                  )}
                  <span className="relative z-10 flex items-center gap-2">
                    {loading ? (
                      <>
                        <LoaderCircle
                          size={16}
                          className="animate-spin"
                          aria-hidden="true"
                        />
                        Signing in…
                      </>
                    ) : (
                      <>
                        Sign in securely
                        <ArrowRight
                          size={16}
                          className="transition-transform group-hover:translate-x-0.5"
                          aria-hidden="true"
                        />
                      </>
                    )}
                  </span>
                </button>

                <div
                  className="mt-5 flex items-center gap-3"
                  aria-hidden="true"
                >
                  <span className="h-px flex-1 bg-gradient-to-r from-transparent to-white/10" />
                  <span className="text-[9px] font-medium uppercase tracking-[0.16em] text-white/25">
                    SOLO.MDSH
                  </span>
                  <span className="h-px flex-1 bg-gradient-to-l from-transparent to-white/10" />
                </div>

                <p className="mt-4 text-center text-sm text-white/[0.42]">
                  Don&apos;t have an account?{" "}
                  <Link
                    to="/register"
                    className="font-medium text-[#84eadf] transition hover:text-[#b4fff4]"
                  >
                    Create one
                  </Link>
                </p>

                <p className="mt-5 flex items-center justify-center gap-2 text-center text-[10px] uppercase tracking-[0.13em] text-white/[0.24]">
                  <ShieldCheck
                    size={12}
                    className="text-[#5ed6ca]"
                    aria-hidden="true"
                  />
                  Protected operational workspace
                </p>
              </div>
            </form>
          </div>
        </section>
      </main>

      <style>{`
        .solo-auth-shell {
          isolation: isolate;
          background:
            radial-gradient(circle at 12% 8%, rgba(34, 230, 211, 0.12), transparent 27%),
            radial-gradient(circle at 88% 10%, rgba(77, 91, 255, 0.17), transparent 30%),
            radial-gradient(circle at 82% 65%, rgba(139, 109, 255, 0.11), transparent 29%),
            linear-gradient(145deg, #061426 0%, #07172b 43%, #050e1e 100%);
        }

        .solo-auth-shell::before {
          content: "";
          position: fixed;
          inset: 0;
          z-index: -1;
          pointer-events: none;
          background:
            linear-gradient(112deg, transparent 0 58%, rgba(255, 255, 255, 0.014) 58.2%, transparent 59%),
            linear-gradient(72deg, transparent 0 74%, rgba(34, 230, 211, 0.018) 74.2%, transparent 75%);
        }

        .solo-auth-aurora {
          position: absolute;
          border-radius: 999px;
          filter: blur(110px);
          opacity: 0.58;
          will-change: transform;
          animation: soloAuthAurora 16s ease-in-out infinite alternate;
        }

        .solo-auth-aurora--teal {
          left: -14%;
          top: -12%;
          width: 48vw;
          height: 48vw;
          min-width: 420px;
          min-height: 420px;
          background: rgba(14, 209, 198, 0.22);
        }

        .solo-auth-aurora--cyan {
          left: 30%;
          bottom: -34%;
          width: 52vw;
          height: 48vw;
          min-width: 460px;
          min-height: 420px;
          background: rgba(38, 158, 255, 0.13);
          animation-delay: -5s;
        }

        .solo-auth-aurora--violet {
          right: -18%;
          top: -16%;
          width: 50vw;
          height: 50vw;
          min-width: 440px;
          min-height: 440px;
          background: rgba(110, 80, 255, 0.23);
          animation-delay: -9s;
        }

        .solo-auth-grid {
          background-image:
            linear-gradient(rgba(104, 232, 255, 0.042) 1px, transparent 1px),
            linear-gradient(90deg, rgba(104, 232, 255, 0.042) 1px, transparent 1px);
          background-size: 64px 64px;
          -webkit-mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.86), transparent 94%);
          mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.86), transparent 94%);
        }

        .solo-auth-stars {
          opacity: 0.26;
          background-image:
            radial-gradient(circle at 15% 18%, rgba(255,255,255,.78) 0 1px, transparent 1.5px),
            radial-gradient(circle at 72% 13%, rgba(108,233,255,.65) 0 1px, transparent 1.5px),
            radial-gradient(circle at 82% 64%, rgba(166,140,255,.65) 0 1px, transparent 1.5px),
            radial-gradient(circle at 36% 73%, rgba(255,255,255,.55) 0 1px, transparent 1.5px),
            radial-gradient(circle at 9% 84%, rgba(72,224,213,.66) 0 1px, transparent 1.5px);
          background-size: 360px 300px, 420px 360px, 510px 390px, 460px 410px, 580px 460px;
          animation: soloAuthStars 18s ease-in-out infinite alternate;
        }

        .solo-auth-noise {
          opacity: 0.022;
          mix-blend-mode: soft-light;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 180 180' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='.55'/%3E%3C/svg%3E");
        }

        .solo-auth-horizon {
          position: absolute;
          left: -12%;
          right: -12%;
          bottom: -55%;
          height: 88%;
          border-radius: 50% 50% 0 0;
          border-top: 1px solid rgba(83, 222, 255, 0.32);
          background:
            radial-gradient(ellipse at top, rgba(43, 204, 255, 0.09), transparent 34%),
            radial-gradient(ellipse at 70% 5%, rgba(146, 91, 255, 0.14), transparent 36%);
          box-shadow: 0 -12px 44px rgba(48, 196, 255, 0.09), 0 -10px 70px rgba(139, 109, 255, 0.08);
        }

        .solo-auth-logo {
          background: linear-gradient(135deg, #8ff4e5, #35e6dc 50%, #57c8ff);
          box-shadow: 0 0 32px rgba(53, 230, 220, 0.18), inset 0 1px 0 rgba(255,255,255,.5);
        }

        .solo-auth-gradient-text {
          color: transparent;
          background: linear-gradient(90deg, #55f0df 0%, #47cfff 48%, #9b7cff 100%);
          -webkit-background-clip: text;
          background-clip: text;
        }

        .solo-auth-connectors path {
          fill: none;
          stroke: url(#solo-auth-line);
          stroke-width: 1.2;
          stroke-dasharray: 5 8;
          vector-effect: non-scaling-stroke;
          animation: soloAuthDash 10s linear infinite;
        }

        .solo-auth-orbit {
          position: absolute;
          left: 50%;
          top: 50%;
          border-radius: 50%;
          border: 1px solid rgba(107, 226, 255, 0.14);
          box-shadow: inset 0 0 34px rgba(65, 203, 255, 0.025), 0 0 28px rgba(91, 98, 255, 0.035);
        }

        .solo-auth-orbit--one {
          width: 52%;
          height: 48%;
          transform: translate(-50%, -50%) rotate(-12deg);
          animation: soloAuthOrbitPulse 7s ease-in-out infinite;
        }

        .solo-auth-orbit--two {
          width: 72%;
          height: 70%;
          transform: translate(-50%, -50%) rotate(16deg);
          border-color: rgba(135, 106, 255, 0.14);
          animation: soloAuthOrbitPulse 9s ease-in-out infinite reverse;
        }

        .solo-auth-orbit--three {
          width: 42%;
          height: 92%;
          transform: translate(-50%, -50%) rotate(62deg);
          border-style: dashed;
          border-color: rgba(46, 235, 215, 0.1);
          animation: soloAuthOrbitSpin 28s linear infinite;
        }

        .solo-auth-core {
          position: absolute;
          left: 50%;
          top: 50%;
          z-index: 3;
          width: 190px;
          height: 142px;
          transform: translate(-50%, -50%) rotateX(8deg);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          border-radius: 28px;
          border: 1px solid rgba(92, 235, 255, 0.32);
          background: linear-gradient(150deg, rgba(8, 38, 51, 0.95), rgba(13, 22, 52, 0.93));
          box-shadow:
            inset 0 0 42px rgba(33, 230, 210, 0.08),
            0 22px 70px rgba(0, 0, 0, 0.4),
            0 0 64px rgba(48, 219, 255, 0.12),
            0 0 95px rgba(130, 85, 255, 0.07);
          animation: soloAuthCoreLift 7s ease-in-out infinite;
        }

        .solo-auth-core::after {
          content: "";
          position: absolute;
          left: 16%;
          right: 16%;
          bottom: -26px;
          height: 12px;
          border-radius: 50%;
          background: linear-gradient(90deg, rgba(34,230,211,.08), rgba(55,200,255,.72), rgba(139,109,255,.52), rgba(34,230,211,.08));
          filter: blur(7px);
        }

        .solo-auth-core-icon {
          display: grid;
          place-items: center;
          width: 42px;
          height: 42px;
          margin-bottom: 10px;
          border-radius: 15px;
          border: 1px solid rgba(45, 235, 216, 0.26);
          background: rgba(34, 230, 211, 0.065);
          color: #8bf6e9;
        }

        .solo-auth-node {
          position: absolute;
          z-index: 2;
          width: 178px;
          min-height: 58px;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 11px 13px;
          border: 1px solid rgba(105, 224, 255, 0.16);
          border-radius: 17px;
          background: linear-gradient(145deg, rgba(7, 27, 39, 0.88), rgba(16, 17, 44, 0.8));
          box-shadow: 0 16px 45px rgba(0, 0, 0, 0.3), inset 0 0 20px rgba(60, 215, 255, 0.03);
          backdrop-filter: blur(12px);
          animation: soloAuthNodeFloat 8s ease-in-out infinite;
        }

        .solo-auth-node > svg {
          flex: none;
          color: #77efe0;
        }

        .solo-auth-node--procurement { left: 2%; top: 3%; }
        .solo-auth-node--receiving { right: 1%; top: 2%; animation-delay: -1.3s; border-color: rgba(140, 111, 255, 0.22); }
        .solo-auth-node--inventory { right: 0; top: 41%; animation-delay: -2.6s; }
        .solo-auth-node--invoices { right: 3%; bottom: 1%; animation-delay: -3.9s; border-color: rgba(140, 111, 255, 0.2); }
        .solo-auth-node--events { left: 3%; bottom: 1%; animation-delay: -5.2s; }
        .solo-auth-node--intelligence { left: 0; top: 41%; animation-delay: -6.5s; border-color: rgba(140, 111, 255, 0.2); }

        .solo-auth-card-halo {
          background:
            radial-gradient(circle at 15% 15%, rgba(34,230,211,.13), transparent 42%),
            radial-gradient(circle at 85% 8%, rgba(139,109,255,.16), transparent 44%);
          filter: blur(26px);
          opacity: .62;
        }

        .solo-auth-card {
          --rx: 0deg;
          --ry: 0deg;
          transform: perspective(1400px) rotateX(var(--rx)) rotateY(var(--ry));
          transform-style: preserve-3d;
          background:
            radial-gradient(circle at 8% 8%, rgba(34, 230, 211, 0.055), transparent 28%),
            radial-gradient(circle at 92% 8%, rgba(139, 109, 255, 0.07), transparent 31%),
            linear-gradient(145deg, rgba(8, 25, 45, 0.88), rgba(7, 15, 33, 0.84));
          box-shadow:
            inset 0 0 58px rgba(51, 166, 255, 0.025),
            0 34px 100px rgba(0, 0, 0, 0.35),
            0 0 50px rgba(52, 177, 255, 0.05);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          transition: transform 220ms ease, border-color 220ms ease, box-shadow 220ms ease;
          will-change: transform;
        }

        .solo-auth-card:focus-within {
          border-color: rgba(110, 232, 222, 0.23);
          box-shadow:
            inset 0 0 58px rgba(51, 166, 255, 0.035),
            0 34px 100px rgba(0, 0, 0, 0.35),
            0 0 60px rgba(52, 205, 207, 0.07);
        }

        .solo-auth-card-shine {
          background: linear-gradient(90deg, transparent 3%, rgba(116, 238, 226, 0.72) 34%, rgba(87, 190, 255, 0.72) 61%, rgba(154, 120, 255, 0.56) 84%, transparent 97%);
          opacity: 0.5;
        }

        .solo-auth-input-shell {
          transition: border-color 180ms ease, background-color 180ms ease, box-shadow 180ms ease;
        }

        .solo-auth-input-shell:focus-within {
          border-color: rgba(88, 232, 218, 0.48);
          background: rgba(7, 27, 43, 0.82);
          box-shadow: 0 0 0 3px rgba(53, 230, 220, 0.07), 0 0 28px rgba(55, 200, 255, 0.055);
        }

        .solo-auth-input:-webkit-autofill,
        .solo-auth-input:-webkit-autofill:hover,
        .solo-auth-input:-webkit-autofill:focus {
          -webkit-text-fill-color: #ffffff;
          caret-color: #ffffff;
          -webkit-box-shadow: 0 0 0 1000px #07172b inset;
          transition: background-color 9999s ease-out 0s;
        }

        .solo-auth-submit-glow {
          background: linear-gradient(105deg, transparent 18%, rgba(255,255,255,.34) 45%, transparent 68%);
          transform: translateX(-120%);
          transition: transform 650ms ease;
        }

        .solo-auth-submit:hover .solo-auth-submit-glow {
          transform: translateX(120%);
        }

        .solo-auth-modal-backdrop {
          background: rgba(2, 7, 17, 0.78);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
        }

        .solo-auth-modal {
          background:
            radial-gradient(circle at 6% 6%, rgba(34,230,211,.08), transparent 30%),
            radial-gradient(circle at 94% 4%, rgba(139,109,255,.09), transparent 34%),
            linear-gradient(145deg, rgba(8, 25, 45, 0.98), rgba(7, 15, 33, 0.98));
          box-shadow: 0 34px 110px rgba(0,0,0,.5), 0 0 60px rgba(55,200,255,.06);
        }

        @keyframes soloAuthAurora {
          0% { transform: translate3d(-2%, -2%, 0) scale(0.96); }
          100% { transform: translate3d(4%, 3%, 0) scale(1.06); }
        }

        @keyframes soloAuthStars {
          0% { opacity: .18; transform: translateY(0); }
          100% { opacity: .34; transform: translateY(-8px); }
        }

        @keyframes soloAuthDash {
          to { stroke-dashoffset: -130; }
        }

        @keyframes soloAuthOrbitPulse {
          0%, 100% { opacity: .42; scale: .98; }
          50% { opacity: .82; scale: 1.02; }
        }

        @keyframes soloAuthOrbitSpin {
          to { transform: translate(-50%, -50%) rotate(422deg); }
        }

        @keyframes soloAuthCoreLift {
          0%, 100% { transform: translate(-50%, -50%) rotateX(8deg) translateY(-3px); }
          50% { transform: translate(-50%, -50%) rotateX(8deg) translateY(4px); }
        }

        @keyframes soloAuthNodeFloat {
          0%, 100% { transform: translateY(-3px); }
          50% { transform: translateY(4px); }
        }

        @media (max-width: 1279px) and (min-width: 1024px) {
          .solo-auth-visual {
            width: 118%;
            margin-left: -9%;
            transform: scale(.86);
          }

          .solo-auth-node {
            width: 162px;
          }
        }

        @media (max-height: 780px) and (min-width: 1024px) {
          .solo-auth-visual {
            height: 270px;
            margin-top: 8px;
            transform: scale(.9);
          }

          .solo-auth-card {
            padding-top: 1.5rem;
            padding-bottom: 1.5rem;
          }
        }

        @media (max-width: 1023px) {
          .solo-auth-visual {
            display: none;
          }

          .solo-auth-horizon {
            bottom: -63%;
          }

          .solo-auth-card {
            transform: none !important;
          }
        }

        @media (max-width: 640px) {
          .solo-auth-aurora {
            filter: blur(84px);
            opacity: .48;
          }

          .solo-auth-grid {
            background-size: 48px 48px;
            opacity: .7;
          }

          .solo-auth-card {
            border-radius: 25px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .solo-auth-aurora,
          .solo-auth-stars,
          .solo-auth-connectors path,
          .solo-auth-orbit,
          .solo-auth-core,
          .solo-auth-node,
          .solo-auth-submit-glow,
          .animate-spin {
            animation: none !important;
          }

          .solo-auth-card,
          .solo-auth-input-shell,
          .solo-auth-submit,
          .solo-auth-submit-glow,
          .solo-auth-submit * {
            transition: none !important;
          }
        }
      `}</style>
    </div>
  );
}
